import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import PageServices from '../../services/PageServices';
import {
  C, FONT, MARGIN, X_AXIS_HEIGHT, IST_OFFSET_SEC, BADGE_GAP,
  SESSION_START_SEC, SESSION_END_SEC, fmtClock, TimeBadge, IconButton,
} from './OIPercentageGraph';

// OI Charts popup body (AOC style): tabs + one live chart for the clicked strike.
// Data comes from POST /history/strikeCharts { chart, … } and is refreshed every REFRESH_MS
// while the popup is open, so the lines, end badges and support / resistance stay current.

const REFRESH_MS = 15_000;
const CHART_HEIGHT = 520;
const AXIS_WIDTH = 76;
const NICE_STEPS = [0.01, 0.02, 0.025, 0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 4, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000];
const TARGET_TICKS = 11;

const GREEN = '#4caf50';
const RED = '#e53935';
const BLUE = '#2f55d4';
const PURPLE = '#9c27b0';
const ORANGE = '#f5a623';

// Millions → "12.34M", or "1.67B" from 1000M
const fmtM = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(2)}B` : `${Number(v).toFixed(2)}M`);
const fmt2 = (v) => Number(v).toFixed(2);

// One entry per tab, in AOC's order. Series: axis 'left' | 'right', hidden = off by default.
// zero: reference line value + axis. table: the strike table's metric (CE / PE columns).
const CHARTS = [
  {
    id: 'oiChange', label: 'OI Chng',
    axes: { right: fmtM }, zero: { axis: 'right', value: 0 },
    series: [
      { key: 'peOIChange', name: 'OIChg PE', color: GREEN },
      { key: 'ceOIChange', name: 'OIChg CE', color: RED },
      { key: 'oiChgDifference', name: 'OIChg(PE-CE)', color: BLUE },
    ],
    table: { ce: 'OIChg CE', pe: 'OIChg PE', diff: 'pe-ce', fmt: fmtM },
  },
  {
    id: 'voi', label: 'VOI',
    axes: { right: fmt2 }, zero: { axis: 'right', value: 0 },
    series: [
      { key: 'voiCE', name: 'Voi CE', color: GREEN },
      { key: 'voiPE', name: 'Voi PE', color: RED },
      { key: 'voiDiff', name: 'Voi(CE-PE)', color: BLUE },
    ],
    table: { ce: 'VOI CE', pe: 'VOI PE', diff: 'ce-pe', fmt: fmt2 },
  },
  {
    id: 'crossOi', label: 'Cross OI',
    axes: { right: fmtM },
    series: null, // built from the strike below / the strike / the strike above
    table: { ce: 'OIChg CE', pe: 'OIChg PE', diff: 'pe-ce', fmt: fmtM },
  },
  {
    id: 'pcr', label: 'PCR',
    axes: { right: fmt2 }, zero: { axis: 'right', value: 1 },
    series: [
      { key: 'pcrOI', name: 'PCR OI', color: BLUE },
      { key: 'pcrOIChange', name: 'PCR OIChng', color: ORANGE },
    ],
    table: { ce: 'OI CE', pe: 'OI PE', diff: 'pe-ce', fmt: fmtM },
  },
  {
    id: 'oi', label: 'OI',
    axes: { right: fmtM }, zero: { axis: 'right', value: 0 },
    series: [
      { key: 'peOI', name: 'OI PE', color: GREEN },
      { key: 'ceOI', name: 'OI CE', color: RED },
      { key: 'oiDifference', name: 'OI (PE-CE)', color: BLUE },
    ],
    table: { ce: 'OI CE', pe: 'OI PE', diff: 'pe-ce', fmt: fmtM },
  },
  {
    id: 'ceBuildup', label: 'CE BuildUp',
    axes: { left: fmtM, right: fmt2 },
    series: [
      { key: 'oiChange', name: 'OI Chng', color: PURPLE, axis: 'left' },
      { key: 'oi', name: 'OI', color: '#a0522d', axis: 'left', hidden: true },
      { key: 'ltp', name: 'LTP', color: GREEN, axis: 'right' },
    ],
    table: { ce: 'OIChg CE', pe: 'OIChg PE', diff: 'pe-ce', fmt: fmtM },
  },
  {
    id: 'peBuildup', label: 'PE BuildUp',
    axes: { left: fmtM, right: fmt2 },
    series: [
      { key: 'oiChange', name: 'OI Change', color: PURPLE, axis: 'left' },
      { key: 'oi', name: 'OI', color: '#8d8a3a', axis: 'left', hidden: true },
      { key: 'ltp', name: 'LTP', color: RED, axis: 'right' },
    ],
    table: { ce: 'OIChg CE', pe: 'OIChg PE', diff: 'pe-ce', fmt: fmtM },
  },
  {
    id: 'coiPct', label: 'COI%',
    axes: { right: fmt2 },
    series: [
      { key: 'coiCE', name: 'COI CE', color: RED },
      { key: 'coiPE', name: 'COI PE', color: GREEN },
      { key: 'coiDiff', name: 'COI PE-CE', color: BLUE },
    ],
    table: { ce: 'COI% CE', pe: 'COI% PE', diff: 'pe-ce', fmt: fmt2 },
  },
  {
    id: 'volume', label: 'Volume',
    axes: { left: fmtM, right: fmt2 }, zero: { axis: 'left', value: 0 },
    series: [
      { key: 'volPE', name: 'Vol PE', color: GREEN, axis: 'left' },
      { key: 'volCE', name: 'Vol CE', color: RED, axis: 'left' },
      { key: 'volDiff', name: 'Vol(Ce-Pe)', color: BLUE, axis: 'left' },
      { key: 'volRatio', name: 'Vol CE/PE', color: PURPLE, axis: 'right', hidden: true },
      { key: 'ltpCE', name: 'LTP CE', color: GREEN, axis: 'right', hidden: true },
      { key: 'ltpPE', name: 'LTP PE', color: RED, axis: 'right', hidden: true },
    ],
    table: { ce: 'Vol CE', pe: 'Vol PE', diff: 'ce-pe', fmt: fmtM },
  },
];

// Cross OI: CE solid (reds), PE dashed (greens); the strike's CE and the lower strike's PE shown by default.
const crossOiSeries = ([lo, k, hi] = []) => {
  const list = [];
  [[hi, '#b71c1c'], [k, RED], [lo, '#ff8a80']].forEach(([strike, color]) => {
    if (strike != null) list.push({ key: `CE_${strike}`, name: `${strike} CE`, color, hidden: strike !== k });
  });
  [[hi, '#2e7d32'], [k, '#81c784'], [lo, GREEN]].forEach(([strike, color]) => {
    if (strike != null) list.push({ key: `PE_${strike}`, name: `${strike} PE`, color, dashed: true, hidden: strike !== lo });
  });
  return list;
};

const istDateParam = () => {
  const d = new Date(Date.now() + IST_OFFSET_SEC * 1000);
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }).toLowerCase();
  return `${d.getUTCDate()}-${month}-${d.getUTCFullYear()}`;
};

// "Nice" ticks for one axis, always including `include` (the zero / reference line).
const niceScale = (values, include) => {
  const lo = Math.min(include ?? Infinity, ...values);
  const hi = Math.max(include ?? -Infinity, ...values);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { min: 0, max: 1, ticks: [0, 1] };
  const raw = (hi - lo || Math.abs(hi) || 1) / TARGET_TICKS;
  const step = NICE_STEPS.find((s) => s >= raw) || NICE_STEPS[NICE_STEPS.length - 1];
  const min = Math.floor(lo / step) * step - step;
  const max = Math.ceil(hi / step) * step + step;
  const ticks = [];
  for (let v = min; v <= max + step / 2; v += step) ticks.push(Math.round(v * 1000) / 1000);
  return { min, max, ticks };
};

// Value pill on the left or right axis
const AxisBadge = ({ viewBox, value, fill, textColor = '#fff', dy = 0, side = 'right', fmt = fmt2 }) => {
  if (!viewBox) return null;
  const x = side === 'right' ? viewBox.x + viewBox.width + 2 : viewBox.x - AXIS_WIDTH + 2;
  const y = viewBox.y + dy;
  return (
    <g>
      <rect x={x} y={y - 11} width={AXIS_WIDTH - 4} height={22} rx={3} fill={fill} />
      <text x={x + (AXIS_WIDTH - 4) / 2} y={y + 5} textAnchor="middle" fill={textColor} fontSize={13} fontWeight={600}>
        {fmt(value)}
      </text>
    </g>
  );
};

// Reference line label: "Zero Line" box next to the value badge on its axis
const ZeroLabel = ({ viewBox, dy = 0, side, value, fmt }) => {
  if (!viewBox) return null;
  const y = viewBox.y + dy;
  const boxX = side === 'right' ? viewBox.x + viewBox.width - 96 : viewBox.x + 4;
  return (
    <g>
      <rect x={boxX} y={y - 11} width={92} height={22} rx={2} fill="#e6e6e6" />
      <text x={boxX + 46} y={y + 5} textAnchor="middle" fill="#111" fontSize={13} fontWeight={500}>Zero Line</text>
      <AxisBadge viewBox={{ ...viewBox, y }} value={value} fill="#e6e6e6" textColor="#111" side={side} fmt={fmt} />
    </g>
  );
};

const XTick = ({ x, y, payload }) => (
  <text x={x} y={y + 16} textAnchor="middle" fill={C.text} fontSize={13} fontWeight={700}>
    {fmtClock(payload.value, false)}
  </text>
);

const HoverTooltip = ({ active, payload, label, formats }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      color: C.text, fontSize: 13, lineHeight: '20px', pointerEvents: 'none',
      background: 'rgba(19, 19, 19, 0.88)', padding: '8px 12px', borderRadius: 2,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{fmtClock(label, false)}</div>
      {payload.filter((p) => p.value != null).map((p) => (
        <div key={p.dataKey}>{p.name}: {(formats[p.dataKey] || fmt2)(p.value)}</div>
      ))}
    </div>
  );
};

const StrikeTable = ({ data, config }) => {
  const rows = data?.table?.rows || [];
  if (!rows.length) return <p style={{ textAlign: 'center', padding: 80, color: C.muted, margin: 0 }}>No data available</p>;
  const { fmt } = config.table;
  const support = new Set((data.support || []).map((l) => l.strikePrice));
  const resistance = new Set((data.resistance || []).map((l) => l.strikePrice));
  const maxAbs = Math.max(1e-9, ...rows.flatMap((r) => [Math.abs(r.ce || 0), Math.abs(r.pe || 0)]));
  const diffOf = (r) => (r.ce == null || r.pe == null ? null : config.table.diff === 'ce-pe' ? r.ce - r.pe : r.pe - r.ce);
  const bar = (v, color) => (
    <span style={{
      display: 'inline-block', height: 10, borderRadius: 2, background: color,
      width: `${(Math.abs(v || 0) / maxAbs) * 120}px`, opacity: v < 0 ? 0.45 : 1,
    }} />
  );
  const th = { padding: '8px 10px', fontWeight: 600 };
  return (
    <div style={{ maxHeight: CHART_HEIGHT, overflowY: 'auto', padding: '8px 16px 16px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ position: 'sticky', top: 0, background: C.bg }}>
            <th style={{ ...th, textAlign: 'right', color: RED, width: '30%' }}>{config.table.ce}</th>
            <th style={{ ...th, textAlign: 'center', color: C.muted, fontWeight: 500 }}>Strike</th>
            <th style={{ ...th, textAlign: 'left', color: GREEN, width: '30%' }}>{config.table.pe}</th>
            <th style={{ ...th, textAlign: 'right', color: '#5b8def' }}>{config.table.diff === 'ce-pe' ? 'CE − PE' : 'PE − CE'}</th>
            <th style={{ ...th, textAlign: 'center', color: C.muted, fontWeight: 500 }}>Level</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const selected = r.strikePrice === Number(data.strikePrice);
            const level = [support.has(r.strikePrice) && 'Support', resistance.has(r.strikePrice) && 'Resistance']
              .filter(Boolean).join(' / ');
            const diff = diffOf(r);
            return (
              <tr key={r.strikePrice} style={{ borderTop: `1px solid ${C.grid}`, background: selected ? '#1f2a3d' : 'transparent' }}>
                <td style={{ padding: '5px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.ce == null ? '—' : fmt(r.ce)}</span>
                    {bar(r.ce, RED)}
                  </div>
                </td>
                <td style={{ textAlign: 'center', padding: '5px 10px', fontWeight: selected ? 700 : 500 }}>{r.strikePrice}</td>
                <td style={{ padding: '5px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {bar(r.pe, GREEN)}
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.pe == null ? '—' : fmt(r.pe)}</span>
                  </div>
                </td>
                <td style={{ textAlign: 'right', padding: '5px 10px', fontVariantNumeric: 'tabular-nums', color: (diff ?? 0) >= 0 ? GREEN : RED }}>
                  {diff == null ? '—' : fmt(diff)}
                </td>
                <td style={{ textAlign: 'center', padding: '5px 10px', color: level.startsWith('Support') ? GREEN : RED }}>{level}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Toolbar row: view icons (left), legend (centre)
const ToolbarRow = ({ toolbar, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', padding: '10px 12px 4px' }}>
    <div style={{ display: 'flex', gap: 8, width: 120, flexShrink: 0 }}>{toolbar}</div>
    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap' }}>{children}</div>
    <div style={{ width: 120, flexShrink: 0 }} />
  </div>
);

const StrikeChart = ({ data, config, series, visible, onToggle, toolbar }) => {
  const [hover, setHover] = useState(null); // { ts, chartY }
  const rows = useMemo(() => data?.points || [], [data]);
  const last = rows[rows.length - 1];
  const axisOf = (s) => s.axis || 'right';
  const lines = series.filter((s) => visible[s.key]);
  const axisIds = ['left', 'right'].filter((a) => config.axes[a]);
  const formats = Object.fromEntries(series.map((s) => [s.key, config.axes[axisOf(s)]]));

  // Domain per axis from its visible lines (all of its lines when none is visible)
  const scales = useMemo(() => {
    const out = {};
    axisIds.forEach((axis) => {
      const own = series.filter((s) => axisOf(s) === axis);
      const shown = own.filter((s) => visible[s.key]);
      const keys = (shown.length ? shown : own).map((s) => s.key);
      const vals = rows.flatMap((d) => keys.map((k) => d[k]).filter((v) => v != null && Number.isFinite(v)));
      const include = config.zero?.axis === axis ? config.zero.value : undefined;
      out[axis] = niceScale(vals.length ? vals : [0], include);
    });
    return out;
  }, [rows, series, visible, config]); // eslint-disable-line react-hooks/exhaustive-deps

  const session = useMemo(() => {
    if (!rows.length) return null;
    const dayStart = Math.floor((rows[0].time + IST_OFFSET_SEC) / 86400) * 86400 - IST_OFFSET_SEC;
    return {
      start: Math.min(dayStart + SESSION_START_SEC, rows[0].time),
      end: Math.max(dayStart + SESSION_END_SEC, rows[rows.length - 1].time),
    };
  }, [rows]);

  // Every hour at :30 inside the session
  const xTicks = useMemo(() => {
    if (!session) return [];
    const t = [];
    const first = Math.ceil((session.start + IST_OFFSET_SEC - 1800) / 3600) * 3600 + 1800 - IST_OFFSET_SEC;
    for (let ts = first; ts <= session.end; ts += 3600) t.push(ts);
    return t;
  }, [session]);

  const plotTop = MARGIN.top;
  const plotBottom = CHART_HEIGHT - MARGIN.bottom - X_AXIS_HEIGHT;
  const valueAt = (axis, chartY) => {
    const { min, max } = scales[axis];
    const r = Math.min(Math.max((chartY - plotTop) / (plotBottom - plotTop), 0), 1);
    return max - r * (max - min);
  };

  // Stack each axis's badges (reference line + last values) so they never overlap
  const badgeShift = useMemo(() => {
    const shift = {};
    axisIds.forEach((axis) => {
      const { min, max } = scales[axis];
      const pxPerUnit = (plotBottom - plotTop) / (max - min);
      const items = [];
      if (config.zero?.axis === axis) items.push({ id: '__zero', value: config.zero.value });
      if (last) lines.filter((s) => axisOf(s) === axis && last[s.key] != null).forEach((s) => items.push({ id: s.key, value: last[s.key] }));
      let prev = -Infinity;
      items
        .map((it) => ({ ...it, y: plotTop + (max - it.value) * pxPerUnit }))
        .sort((a, b) => a.y - b.y)
        .forEach((it) => {
          const y = Math.max(it.y, prev + BADGE_GAP);
          shift[it.id] = y - it.y;
          prev = y;
        });
    });
    return shift;
  }, [scales, last, lines, config]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMove = (state) => {
    if (!state?.isTooltipActive || state.activeLabel == null || state.chartY == null) return setHover(null);
    setHover({ ts: state.activeLabel, chartY: state.chartY });
  };

  return (
    <>
      <ToolbarRow toolbar={toolbar}>
        {series.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => onToggle(s.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: 'none',
              cursor: 'pointer', color: C.text, fontSize: 17, opacity: visible[s.key] ? 1 : 0.35, padding: 0,
            }}
          >
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
            {s.name}
          </button>
        ))}
      </ToolbarRow>

      {!rows.length ? (
        <p style={{ textAlign: 'center', padding: 80, color: C.muted, margin: 0 }}>No data available</p>
      ) : (
        <div style={{ position: 'relative' }}>
          {data?.strikePrice != null && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none', color: '#2a2a2a', fontWeight: 800, fontSize: 56, userSelect: 'none',
              paddingBottom: X_AXIS_HEIGHT,
            }}>
              {data.strikePrice}
            </div>
          )}
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart data={rows} margin={MARGIN} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
              <CartesianGrid stroke={C.grid} strokeDasharray="0" yAxisId={axisIds[axisIds.length - 1]} />
              <XAxis
                dataKey="time"
                type="number"
                scale="time"
                domain={session ? [session.start, session.end] : ['dataMin', 'dataMax']}
                allowDataOverflow
                ticks={xTicks}
                tick={<XTick />}
                tickLine={false}
                axisLine={{ stroke: C.grid }}
                height={X_AXIS_HEIGHT}
              />
              {axisIds.map((axis) => (
                <YAxis
                  key={axis}
                  yAxisId={axis}
                  orientation={axis}
                  domain={[scales[axis].min, scales[axis].max]}
                  ticks={scales[axis].ticks}
                  allowDataOverflow
                  tickFormatter={config.axes[axis]}
                  tick={{ fill: C.text, fontSize: 13 }}
                  tickLine={false}
                  axisLine={false}
                  width={AXIS_WIDTH}
                />
              ))}

              {/* Reference ("Zero") line */}
              {config.zero && (
                <ReferenceLine
                  yAxisId={config.zero.axis}
                  y={config.zero.value}
                  stroke="#e6e6e6"
                  strokeDasharray="6 4"
                  strokeWidth={1.5}
                  label={<ZeroLabel dy={badgeShift.__zero} side={config.zero.axis} value={config.zero.value} fmt={config.axes[config.zero.axis]} />}
                />
              )}

              {/* Last value of each visible line: dotted guide + badge on its axis */}
              {last && lines.filter((s) => last[s.key] != null).map((s) => (
                <ReferenceLine
                  key={`last-${s.key}`}
                  yAxisId={axisOf(s)}
                  y={last[s.key]}
                  stroke={s.color}
                  strokeDasharray="2 3"
                  strokeWidth={1}
                  label={<AxisBadge value={last[s.key]} fill={s.color} dy={badgeShift[s.key]} side={axisOf(s)} fmt={config.axes[axisOf(s)]} />}
                />
              ))}

              {/* Crosshair: value badge on every axis + time badge */}
              {hover && axisIds.map((axis) => {
                const value = valueAt(axis, hover.chartY);
                return (
                  <ReferenceLine
                    key={`hover-${axis}`}
                    yAxisId={axis}
                    y={value}
                    stroke="#9a9a9a"
                    strokeDasharray="2 3"
                    label={<AxisBadge value={value} fill="#e6e6e6" textColor="#111" side={axis} fmt={config.axes[axis]} />}
                  />
                );
              })}
              {hover && (
                <ReferenceLine
                  yAxisId={axisIds[0]}
                  x={hover.ts}
                  stroke="transparent"
                  label={<TimeBadge label={fmtClock(hover.ts, false)} />}
                />
              )}

              <Tooltip
                content={<HoverTooltip formats={formats} />}
                cursor={{ stroke: '#9a9a9a', strokeWidth: 1, strokeDasharray: '2 3' }}
                offset={24}
                isAnimationActive={false}
              />

              {lines.map((s) => (
                <Line
                  key={s.key}
                  yAxisId={axisOf(s)}
                  type="linear"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  strokeDasharray={s.dashed ? '4 3' : undefined}
                  dot={false}
                  connectNulls
                  activeDot={{ r: 5, fill: s.color, stroke: 'none' }}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
};

// `date` / `time` (optional, Backtester): that day up to that time, loaded once (no refresh).
// Without them: today's session, refreshed every REFRESH_MS.
const OIChartsPanel = ({ instrumentIdentifier, date, time }) => {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [chartId, setChartId] = useState('oiChange');
  const [view, setView] = useState('chart');
  const [cache, setCache] = useState({}); // `${instrument}|${chart}` -> response data
  const [loading, setLoading] = useState(false);
  const [toggles, setToggles] = useState({}); // chart -> { seriesKey: visible } (user changes only)
  const inFlight = useRef(false);

  const config = CHARTS.find((c) => c.id === chartId);
  const cacheKey = `${instrumentIdentifier}|${date || ''}|${time || ''}|${chartId}`;
  const data = cache[cacheKey];

  // Follow the surrounding Bootstrap modal: fetch / refresh only while it is open
  useEffect(() => {
    const modal = rootRef.current?.closest('.modal');
    if (!modal) return undefined;
    const onShow = () => setOpen(true);
    const onHide = () => setOpen(false);
    modal.addEventListener('show.bs.modal', onShow);
    modal.addEventListener('hidden.bs.modal', onHide);
    return () => {
      modal.removeEventListener('show.bs.modal', onShow);
      modal.removeEventListener('hidden.bs.modal', onHide);
    };
  }, []);

  // A new strike starts from fresh data and default series
  useEffect(() => {
    setCache({});
    setToggles({});
  }, [instrumentIdentifier, date, time]);

  // Load the active tab now and every REFRESH_MS while open; responses for an older tab / strike are dropped
  useEffect(() => {
    if (!open || !instrumentIdentifier) return undefined;
    let cancelled = false;
    const key = `${instrumentIdentifier}|${date || ''}|${time || ''}|${chartId}`;
    const load = () => {
      if (inFlight.current) return;
      inFlight.current = true;
      PageServices.getStrikeCharts({
        chart: chartId,
        instrumentIdentifier,
        date: date || istDateParam(),
        ...(date && time ? { time } : {}),
        symbolToken: localStorage.getItem('stock'),
      })
        .then((response) => {
          if (!cancelled) setCache((prev) => ({ ...prev, [key]: response?.data || null }));
        })
        .catch(() => {
          if (!cancelled) setCache((prev) => (key in prev ? prev : { ...prev, [key]: null }));
        })
        .finally(() => {
          inFlight.current = false;
          if (!cancelled) setLoading(false);
        });
    };
    setLoading(true);
    load();
    const id = date ? null : setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      inFlight.current = false;
      if (id) clearInterval(id);
    };
  }, [open, instrumentIdentifier, chartId, date, time]);

  const series = useMemo(
    () => config.series || crossOiSeries(data?.strikes),
    [config, data?.strikes] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const visible = useMemo(() => {
    const own = toggles[chartId] || {};
    return Object.fromEntries(series.map((s) => [s.key, own[s.key] ?? !s.hidden]));
  }, [series, toggles, chartId]);
  const toggle = (key) => setToggles((prev) => ({
    ...prev,
    [chartId]: { ...(prev[chartId] || {}), [key]: !visible[key] },
  }));

  const toolbar = (
    <>
      <IconButton title="Chart view" active={view === 'chart'} onClick={() => setView('chart')}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 17 9 11 13 15 21 7" />
        </svg>
      </IconButton>
      <IconButton title="Strike-wise values with support / resistance" active={view === 'table'} onClick={() => setView('table')}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <rect x="3" y="3" width="18" height="4" rx="1" />
          <rect x="3" y="9" width="5" height="12" rx="1" />
          <rect x="9.5" y="9" width="5" height="12" rx="1" />
          <rect x="16" y="9" width="5" height="12" rx="1" />
        </svg>
      </IconButton>
    </>
  );

  return (
    <div ref={rootRef}>
      <div className="modal-body">
        <ul className="nav nav-pills ai-chart-nav" role="tablist">
          {CHARTS.map((c) => (
            <li key={c.id} className="nav-item ai-chart-item" role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={c.id === chartId}
                className={`nav-link${c.id === chartId ? ' active' : ''}`}
                onClick={() => setChartId(c.id)}
              >
                {c.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div style={{ background: C.bg, color: C.text, overflow: 'hidden', fontFamily: FONT }}>
        {loading && data === undefined ? (
          <>
            <ToolbarRow toolbar={toolbar} />
            <p style={{ textAlign: 'center', padding: 80, color: C.muted, margin: 0 }}>Loading…</p>
          </>
        ) : view === 'table' ? (
          <>
            <ToolbarRow toolbar={toolbar} />
            <StrikeTable data={data} config={config} />
          </>
        ) : (
          <StrikeChart data={data} config={config} series={series} visible={visible} onToggle={toggle} toolbar={toolbar} />
        )}
      </div>
    </div>
  );
};

export default OIChartsPanel;
